import { img } from "../images";
import type { Service, ServiceCategory } from "./types";

export const dermatologyCategory: ServiceCategory = {
  slug: "dermatology",
  name: "Dermatology",
  short: "Healthy Skin. Confident You.",
  tagline: "Specialist care for skin, hair and nail health.",
  image: img.dermatology,
  doctorSlug: "dr-m-vanitha",
  intro:
    "Dermatology at AV Clinic focuses on the diagnosis and care of skin, hair and nail conditions. Whether you are dealing with acne, pigmentation, a persistent rash or a chronic skin disorder, specialist evaluation helps identify the underlying cause and plan appropriate, individualised management.",
  description:
    "Your skin is your largest organ — it protects you, reflects your health and is often the first thing others notice. Dermatology deals with the full spectrum of skin, hair and nail concerns, from common conditions like acne and dandruff to chronic disorders such as psoriasis and eczema. At AV Clinic, dermatology care begins with a careful assessment: understanding how the condition started, how it behaves and what may be triggering it. This helps Dr. M. Vanitha tailor treatment to your skin type, lifestyle and goals, rather than applying a one-size-fits-all approach. Whether you need help controlling active acne, managing a long-standing skin condition, or simply guidance on protecting your skin, the aim is clear — healthier, more comfortable skin and the confidence that comes with it.",
  conditions: [
    "acne",
    "acne-scars",
    "pigmentation",
    "melasma",
    "psoriasis",
    "eczema",
    "fungal-infections",
    "vitiligo",
    "urticaria",
    "skin-allergies",
    "nail-disorders",
    "mole-skin-lesion-evaluation",
    "skin-tags",
    "general-dermatology",
  ],
  concerns: [
    "Acne and acne scars",
    "Pigmentation and melasma",
    "Psoriasis and eczema",
    "Fungal and skin infections",
    "Vitiligo and urticaria",
    "Skin allergies and itching",
    "Nail disorders",
    "Moles and skin lesions",
    "Hair loss and scalp conditions",
  ],
  management: [
    "Detailed skin, hair and nail evaluation",
    "Accurate diagnosis of common and chronic skin conditions",
    "Individualised treatment plans suited to your skin type",
    "Guidance on skin care, sun protection and lifestyle",
    "Follow-up care and long-term skin health advice",
  ],
  faqs: [
    {
      q: "How do I know if I need a dermatologist?",
      a: "If you have persistent acne, pigmentation, rashes, itching, hair fall, nail changes or any skin concern that is not improving with self-care, a dermatologist can help identify the cause and plan appropriate care.",
    },
    {
      q: "Are dermatology treatments safe?",
      a: "Treatments are recommended only after a proper evaluation and are tailored to your skin type and condition. Your dermatologist will explain the benefits, expected timeline and any precautions involved.",
    },
    {
      q: "Can I see a dermatologist for preventive skin care?",
      a: "Yes. A dermatology consultation is also useful for sun-protection advice, mole checks and building a sustainable skin-care routine, even when there is no active concern.",
    },
    {
      q: "How long does a dermatology consultation take?",
      a: "A first consultation typically takes 15 to 30 minutes, during which your concern, history and skin are reviewed and a personalised plan is discussed.",
    },
  ],
  related: ["general-dermatology", "acne", "pigmentation", "psoriasis", "hair-loss"],
  diagnosisSteps: [
    { title: "Consultation", desc: "A detailed discussion of your concern, how it started and how it has been behaving." },
    { title: "Medical History & Examination", desc: "Review of relevant health history and careful examination of your skin, hair and nails." },
    { title: "Investigations When Required", desc: "Simple tests such as a skin scraping or wood's lamp may be used when needed — not for every patient." },
    { title: "Individualised Assessment", desc: "Your findings are brought together into a personal skin-care and treatment plan." },
  ],
  specialistApproach: [
    { icon: "ri-headphone-line", title: "Listen", desc: "Understand your concern, history and what you hope to achieve." },
    { icon: "ri-focus-3-line", title: "Assess", desc: "Examine the affected skin, hair and nails in detail." },
    { icon: "ri-stethoscope-line", title: "Diagnose", desc: "Reach an accurate diagnosis, using simple tests where needed." },
    { icon: "ri-calendar-check-line", title: "Plan", desc: "Design a treatment and skin-care plan tailored to you." },
  ],
  patientEducation: [
    { icon: "ri-file-list-3-line", title: "Prepare Before Your Visit", desc: "Note when the concern started, what worsens it, and any products or medicines you use." },
    { icon: "ri-image-2-line", title: "Bring What's Helpful", desc: "Bring previous prescriptions, reports and clear photos of flare-ups if available." },
    { icon: "ri-question-line", title: "Questions to Ask", desc: "Ask about expected timelines, side effects, sun protection and when to return." },
    { icon: "ri-refresh-line", title: "Plan Your Follow-Up", desc: "Many skin conditions improve gradually, so consistent follow-up is key." },
  ],
  myths: [
    { myth: "Acne only affects teenagers.", fact: "Acne can affect people at different ages, including adults, and is influenced by hormones, genetics and lifestyle." },
    { myth: "Scrubbing the skin harder clears acne.", fact: "Over-washing and harsh scrubbing can irritate skin and worsen acne. Gentle cleansing is better." },
    { myth: "Sunscreen is only needed in summer.", fact: "Daily sun protection helps prevent pigmentation, premature ageing and skin damage in all seasons." },
  ],
  seo: {
    title: "Dermatologist in Krishnagiri | Skin Specialist | AV Clinic",
    description:
      "Specialist dermatology in Krishnagiri — acne, pigmentation, psoriasis, eczema, fungal infections, hair & nail care by Dr. M. Vanitha at AV Clinic. Book a consultation.",
  },
};

export const dermatologyServices: Service[] = [
  {
    slug: "general-dermatology",
    name: "General Dermatology",
    category: "Dermatology",
    categorySlug: "dermatology",
    blurb: "Complete evaluation and care for a wide range of skin conditions.",
    intro:
      "General dermatology at AV Clinic covers the diagnosis and management of common and chronic skin conditions, helping you maintain healthy, comfortable skin.",
    symptoms: [
      { name: "Persistent rashes", desc: "Red, scaly or itchy areas that do not settle with basic care." },
      { name: "Itching", desc: "Discomfort that may be localised or widespread and can disturb sleep." },
      { name: "Pigmentation changes", desc: "Dark patches or uneven tone that bother you." },
      { name: "Pimples & breakouts", desc: "Recurrent acne that is not responding to self-care." },
      { name: "Dryness or flaking", desc: "Rough, flaky or tight-feeling skin." },
      { name: "Moles or growths", desc: "New or changing skin lesions you are unsure about." },
    ],
    causes: [
      "Genetics and family history",
      "Hormonal changes",
      "Environmental factors and climate",
      "Allergens and irritants",
      "Infections and skin-care habits",
    ],
    treatment: {
      medical: [
        "Accurate diagnosis of the specific condition",
        "Topical treatments suited to your skin",
        "Oral medication where appropriate",
        "Guidance on appropriate skin-care products",
      ],
      procedures: [],
      lifestyle: [
        "Gentle cleansing and moisturising routine",
        "Consistent sun protection",
        "Avoiding known triggers",
      ],
      followUp:
        "Follow-up depends on the condition and your response to treatment. Most plans are reviewed within a few weeks to a few months.",
    },
    management: [
      "Full skin examination",
      "Accurate diagnosis",
      "Individualised treatment plan",
      "Skin care and prevention advice",
    ],
    faqs: [
      { q: "What happens in a first skin consultation?", a: "Your skin is examined and discussed with you, and any needed treatment or care plan is explained clearly." },
      { q: "Do I need tests for a skin problem?", a: "Not always. Simple tests may be used when the cause is unclear, but most conditions are diagnosed on clinical examination." },
    ],
    related: ["acne", "pigmentation", "psoriasis"],
    relatedBlogs: ["why-see-a-dermatologist-in-krishnagiri"],
    seo: {
      title: "General Dermatology in Krishnagiri | Skin Specialist | AV Clinic",
      description:
        "General dermatology in Krishnagiri — diagnosis and care for rashes, itching, pigmentation and skin infections. Specialist consultation at AV Clinic.",
    },
  },
  {
    slug: "acne",
    name: "Acne Treatment",
    category: "Dermatology",
    categorySlug: "dermatology",
    blurb: "Personalised care for active acne to control breakouts and protect your skin.",
    tagline: "Understanding Acne. Finding the Right Care.",
    intro:
      "Acne is one of the most common skin concerns, affecting people of all ages. At AV Clinic, acne is evaluated by type and severity, and managed with a plan suited to your skin to reduce breakouts, prevent scarring and restore your confidence.",
    image: img.serviceDermatologist,
    symptoms: [
      { name: "Whiteheads", desc: "Small, closed bumps where pores are blocked." },
      { name: "Blackheads", desc: "Open, darkened bumps on the surface of the skin." },
      { name: "Papules", desc: "Small, red, tender bumps." },
      { name: "Pustules", desc: "Red bumps with a white or yellow centre." },
      { name: "Nodules", desc: "Larger, deeper, often painful lumps." },
      { name: "Cysts", desc: "Deep, painful, pus-filled lesions." },
      { name: "Redness & inflammation", desc: "Generalised redness around active breakouts." },
      { name: "Post-acne marks", desc: "Dark spots that remain after acne settles." },
    ],
    causes: [
      "Excess oil (sebum) production",
      "Blocked hair follicles",
      "Bacterial overgrowth on the skin",
      "Hormonal changes (teens, periods, stress)",
      "Genetics and family history",
      "Certain cosmetics or products",
    ],
    types: [
      { name: "Comedonal acne", desc: "Predominantly blackheads and whiteheads with little inflammation." },
      { name: "Inflammatory acne", desc: "Red, swollen papules and pustules." },
      { name: "Nodulocystic acne", desc: "Deep, painful nodules and cysts that carry a higher risk of scarring." },
    ],
    riskFactors: [
      "Family history of acne",
      "Hormonal changes or imbalance",
      "Stress and lack of sleep",
      "Comedogenic cosmetics",
      "Habits such as picking or squeezing",
    ],
    whenToSee: [
      "Breakouts are persistent or keep returning",
      "Acne is inflamed, painful or deep",
      "Over-the-counter products are not helping",
      "Acne is affecting your confidence or mood",
      "You have dark marks or scars after acne",
    ],
    diagnosisSteps: [
      { title: "Skin Assessment", desc: "Your acne is classified by type and severity." },
      { title: "History Review", desc: "Hormonal, lifestyle and product-related factors are considered." },
      { title: "Trigger Check", desc: "Possible triggers that worsen your acne are identified." },
      { title: "Personalised Plan", desc: "A treatment and skin-care plan is designed for your skin." },
    ],
    treatment: {
      medical: [
        "Topical treatments to unclog pores and calm inflammation",
        "Oral medication where appropriate",
        "Adjustment of the plan based on response",
      ],
      procedures: [],
      lifestyle: [
        "Gentle cleansing twice daily",
        "Non-comedogenic, oil-free products",
        "Avoiding picking or squeezing pimples",
        "Managing stress and sleep",
      ],
      followUp:
        "Acne responds gradually. You will usually be reviewed after a few weeks to assess response and adjust the plan, with follow-up continuing until skin is consistently clearer.",
    },
    myths: [
      { myth: "Acne is only a teenage problem.", fact: "Acne can affect people at different ages, including adults, and often relates to hormones, stress and genetics." },
      { myth: "Squeezing pimples clears them faster.", fact: "Squeezing can push infection deeper and cause scarring. It is best to leave lesions to professional care." },
      { myth: "Sun exposure dries up acne.", fact: "Sun can temporarily mask redness but worsens inflammation and leaves dark marks. Sunscreen is essential." },
    ],
    prevention: [
      "Cleanse gently and consistently",
      "Use oil-free, non-comedogenic products",
      "Apply sunscreen daily",
      "Avoid touching your face",
      "Follow prescribed treatment consistently",
    ],
    warningSigns: [
      "Acne becomes deep, painful or widespread",
      "Lesions are not improving despite treatment",
      "You are developing dark marks or scars",
      "Acne is causing significant distress",
    ],
    management: [
      "Assessment of acne type and severity",
      "Medication and topical care suited to your skin",
      "Guidance on cleansing and skin care",
      "Advice to minimise post-acne marks",
    ],
    faqs: [
      { q: "How long does acne treatment take?", a: "Acne responds gradually. Improvement is usually seen over several weeks, and consistent follow-up helps achieve and maintain clear skin." },
      { q: "Can adults develop acne?", a: "Yes. Adult acne is common and can be related to hormones, stress, products or genetics." },
      { q: "Will acne leave permanent scars?", a: "Prompt, consistent treatment and avoiding picking reduce the risk of scarring. Existing marks can often be improved." },
      { q: "Do I need to stop all make-up?", a: "Not necessarily. Choosing oil-free, non-comedogenic products and removing them gently can help." },
      { q: "Can my diet affect acne?", a: "Diet affects people differently. A balanced diet supports skin health, but acne is rarely caused by diet alone." },
      { q: "How can I book a consultation?", a: "You can book through the appointment page, call the clinic, or message us on WhatsApp." },
    ],
    related: ["acne-scars", "general-dermatology", "pigmentation", "chemical-peels"],
    relatedBlogs: ["understanding-acne-causes-and-care", "understanding-acne-scars"],
    seo: {
      title: "Acne Treatment in Krishnagiri | Acne Specialist | AV Clinic",
      description:
        "Personalised acne treatment in Krishnagiri — control breakouts, reduce inflammation and prevent scarring with a dermatologist at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "acne-scars",
    name: "Acne Scars",
    category: "Dermatology",
    categorySlug: "dermatology",
    blurb: "Improving the appearance of marks and scars left by acne.",
    tagline: "Smoother Skin After Acne.",
    intro:
      "After active acne settles, scars and dark marks can remain and be a lasting reminder of a difficult period. Dermatological care helps reduce their appearance through a combination of treatments, resurfacing approaches and skin care — with realistic, gradual goals.",
    image: img.serviceDermatologist,
    symptoms: [
      { name: "Pitted / depressed scars", desc: "Small indentations left after inflamed acne heals." },
      { name: "Dark post-acne marks", desc: "Flat brown or red spots (post-inflammatory hyperpigmentation)." },
      { name: "Raised / hypertrophic scars", desc: "Lumpy or raised scars in some skin types." },
      { name: "Uneven skin texture", desc: "A rough or irregular surface over affected areas." },
    ],
    causes: [
      "Deeply inflamed acne",
      "Picking or squeezing pimples",
      "Delay in treating active acne",
      "Genetics and skin type",
    ],
    types: [
      { name: "Ice-pick scars", desc: "Narrow, deep pits in the skin." },
      { name: "Boxcar scars", desc: "Wider, shallow depressions with defined edges." },
      { name: "Rolling scars", desc: "Broad, undulating depressions." },
      { name: "Post-inflammatory marks", desc: "Dark or red flat spots that fade over time." },
    ],
    riskFactors: [
      "History of severe or nodular acne",
      "Habit of squeezing pimples",
      "Delayed or inconsistent treatment",
    ],
    whenToSee: [
      "Scars affect your confidence",
      "Dark marks are not fading over time",
      "You want to prevent further scarring from active acne",
    ],
    diagnosisSteps: [
      { title: "Scar Assessment", desc: "The type, depth and extent of scars are evaluated." },
      { title: "Skin & Tone Review", desc: "Skin type and pigmentation are considered." },
      { title: "Treatment Selection", desc: "Suitable options are matched to your scars." },
      { title: "Realistic Plan", desc: "A stepwise plan with clear expectations is agreed." },
    ],
    treatment: {
      medical: [
        "Topical agents to help lighten marks",
        "Skin-care to support healing and texture",
      ],
      procedures: [
        "Chemical peels suited to your skin",
        "Resurfacing and skin-renewal techniques where appropriate",
      ],
      lifestyle: [
        "Strict sun protection to prevent darkening",
        "Avoiding further picking",
        "Gentle skin care",
      ],
      followUp:
        "Scar improvement is gradual and often requires several sessions. Your progress is reviewed and the plan adjusted over time.",
    },
    myths: [
      { myth: "Acne scars can be removed completely overnight.", fact: "Scars can usually be significantly improved, but improvement is gradual and complete removal is not guaranteed." },
      { myth: "Dark marks are permanent scars.", fact: "Most dark post-acne marks fade with time, sun protection and appropriate care." },
    ],
    prevention: [
      "Treat active acne early",
      "Never pick or squeeze pimples",
      "Use daily sun protection",
    ],
    warningSigns: [
      "Scars are painful, itchy or growing",
      "A scar changes colour or bleeds",
    ],
    management: [
      "Assessment of scar type and skin condition",
      "Suitable resurfacing and care options",
      "Pigmentation care for post-acne marks",
      "Realistic, gradual improvement goals",
    ],
    faqs: [
      { q: "Can acne scars be removed completely?", a: "Scars can usually be significantly improved, though complete removal is not guaranteed. A dermatologist can advise the most suitable approach for your scar type." },
      { q: "How many sessions will I need?", a: "This depends on the scar type and depth. A plan is outlined during consultation and adjusted as you respond." },
      { q: "Are scar treatments painful?", a: "Most are well tolerated. Your dermatologist will explain the sensation and aftercare involved." },
      { q: "When should I start scar treatment?", a: "It is often best to control active acne first, then address scars. Your dermatologist will guide the timing." },
    ],
    related: ["acne", "scar-treatment", "chemical-peels", "general-dermatology"],
    relatedBlogs: ["understanding-acne-scars"],
    seo: {
      title: "Acne Scar Treatment in Krishnagiri | Dermatologist | AV Clinic",
      description:
        "Acne scar treatment in Krishnagiri — improve pitted scars and dark marks with dermatology care and chemical peels at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "pigmentation",
    name: "Pigmentation",
    category: "Dermatology",
    categorySlug: "dermatology",
    blurb: "Targeted care for uneven skin tone and dark patches.",
    tagline: "Even Skin Tone, Done Right.",
    intro:
      "Pigmentation refers to areas of skin that become darker than the surrounding skin. It is a very common concern. AV Clinic helps identify the cause of pigmentation and manage it with a personalised plan that combines professional treatment, targeted skin care and strict sun protection.",
    image: img.serviceSkinCare,
    symptoms: [
      { name: "Dark patches on face", desc: "Irregular brown or grey patches, often on cheeks and forehead." },
      { name: "Sun spots", desc: "Small flat brown spots on sun-exposed areas." },
      { name: "Uneven tone", desc: "Patchy areas of darker skin without a clear border." },
      { name: "Post-inflammatory darkening", desc: "Dark marks left after acne, injury or irritation." },
    ],
    causes: [
      "Sun exposure",
      "Hormonal changes (melasma)",
      "Past acne or skin inflammation",
      "Certain medications or products",
      "Genetics",
    ],
    types: [
      { name: "Melasma", desc: "Hormone-related brown patches, typically on the face." },
      { name: "Solar lentigines", desc: "Sun-induced flat brown spots (age/sun spots)." },
      { name: "Post-inflammatory hyperpigmentation", desc: "Darkening after inflammation such as acne." },
      { name: "Freckles", desc: "Small tan spots, often genetic and sun-related." },
    ],
    riskFactors: [
      "Frequent or intense sun exposure",
      "Hormonal changes, including pregnancy",
      "History of acne or skin irritation",
      "Lighter or melanin-rich skin types",
    ],
    whenToSee: [
      "Pigmentation is worsening or spreading",
      "Self-care is not helping",
      "You are unsure of the type of pigmentation",
      "You want a safe, effective plan",
    ],
    diagnosisSteps: [
      { title: "Examination", desc: "The type, extent and depth of pigmentation are assessed." },
      { title: "Trigger Review", desc: "Sun, hormonal and product-related factors are considered." },
      { title: "Wood's Lamp Check", desc: "A simple lamp may help assess pigment depth when needed." },
      { title: "Personalised Plan", desc: "A treatment, skin-care and sun-protection plan is designed." },
    ],
    treatment: {
      medical: [
        "Topical lightening agents suited to your skin",
        "Skin-care to support even tone",
      ],
      procedures: [
        "Chemical peels appropriate for your skin type",
        "Pigmentation-targeted treatments where suitable",
      ],
      lifestyle: [
        "Daily broad-spectrum sunscreen",
        "Protective clothing and shade",
        "Avoiding picking and harsh scrubs",
      ],
      followUp:
        "Pigmentation improves gradually. Regular reviews help adjust treatment and maintain results over time.",
    },
    myths: [
      { myth: "Pigmentation disappears quickly with one treatment.", fact: "Pigmentation generally lightens gradually and requires consistent care and sun protection." },
      { myth: "Sunscreen is enough once skin is lighter.", fact: "Continued sun protection is essential to prevent pigmentation from returning." },
    ],
    prevention: [
      "Apply sunscreen daily and reapply",
      "Seek shade during peak sun hours",
      "Wear protective clothing and hats",
      "Treat acne and inflammation early",
    ],
    warningSigns: [
      "A patch changes colour, shape or bleeds",
      "Pigmentation appears with other concerning symptoms",
    ],
    management: [
      "Diagnosis of the type of pigmentation",
      "Topical care and treatment options",
      "Strict sun-protection guidance",
      "Long-term skin brightening plan",
    ],
    faqs: [
      { q: "Why is sun protection important for pigmentation?", a: "Sun exposure can worsen pigmentation. Consistent use of sunscreen is essential to prevent darkening and support treatment." },
      { q: "Can pigmentation be fully removed?", a: "It can usually be significantly lightened, though maintenance may be needed, especially with ongoing sun exposure." },
      { q: "Are lightening treatments safe?", a: "Yes, when prescribed and monitored by a dermatologist for your skin type." },
      { q: "How long before I see a difference?", a: "Most people notice gradual improvement over several weeks to months with consistent care." },
    ],
    related: ["melasma", "pigmentation-peels", "chemical-peels", "general-dermatology"],
    relatedBlogs: ["why-is-my-skin-pigmented"],
    seo: {
      title: "Skin Pigmentation Treatment in Krishnagiri | AV Clinic",
      description:
        "Skin pigmentation treatment in Krishnagiri — dark patches, melasma and uneven tone managed safely with sun protection and dermatology care at AV Clinic.",
    },
  },
  {
    slug: "melasma",
    name: "Melasma",
    category: "Dermatology",
    categorySlug: "dermatology",
    blurb: "Managing the stubborn brown patches of melasma with patience and care.",
    tagline: "Understanding Melasma. Managing It With Care.",
    intro:
      "Melasma causes brown or grey-brown patches, most often on the face, and can be triggered by sun exposure and hormonal changes. It is one of the more stubborn pigmentation concerns, so it is managed with a consistent, multi-angle approach and realistic expectations.",
    image: img.serviceSkinCare,
    symptoms: [
      { name: "Brown patches on cheeks", desc: "Symmetrical darkening across the cheeks and bridge of the nose." },
      { name: "Forehead patches", desc: "Dark areas across the forehead." },
      { name: "Upper lip darkening", desc: "A dark band above the upper lip." },
      { name: "Worsening with sun", desc: "Patches darken noticeably with sun exposure." },
    ],
    causes: [
      "Sun exposure (a major trigger)",
      "Hormonal changes (pregnancy, contraceptives, thyroid)",
      "Genetics",
      "Certain cosmetics or skin irritation",
    ],
    riskFactors: [
      "Female gender",
      "Pregnancy or hormonal therapy",
      "Frequent sun exposure without protection",
      "Family history of melasma",
    ],
    whenToSee: [
      "Facial darkening is persistent or spreading",
      "You are unsure whether it is melasma",
      "You want a safe, structured treatment plan",
    ],
    diagnosisSteps: [
      { title: "Clinical Assessment", desc: "The pattern of facial pigmentation is examined." },
      { title: "Trigger Review", desc: "Sun, hormonal and product factors are considered." },
      { title: "Depth Check", desc: "A wood's lamp may help assess how deep the pigment is." },
      { title: "Careful Plan", desc: "A patient, combined treatment approach is agreed." },
    ],
    treatment: {
      medical: [
        "Topical care to lighten patches gradually",
        "Skin-protection and gentle care routine",
      ],
      procedures: [
        "Pigmentation peels selected carefully for your skin",
      ],
      lifestyle: [
        "Relentless sun protection",
        "Avoiding heat and harsh products",
        "Patience with a consistent routine",
      ],
      followUp:
        "Melasma needs ongoing management. Regular reviews allow the plan to be adjusted and results maintained.",
    },
    myths: [
      { myth: "Melasma can be cured quickly.", fact: "Melasma can often be significantly lightened, but it tends to recur and needs consistent management." },
      { myth: "Melasma is caused by dirt.", fact: "Melasma is related to sun exposure and hormones, not cleanliness." },
    ],
    prevention: [
      "Daily broad-spectrum sunscreen",
      "Wide-brimmed hats and shade",
      "Avoiding harsh or irritating products",
    ],
    management: [
      "Assessment of triggers",
      "Individualised topical and treatment plan",
      "Sun-protection counselling",
      "Realistic expectations and follow-up",
    ],
    faqs: [
      { q: "Can melasma be cured?", a: "Melasma can often be significantly lightened, but it can recur. Consistent treatment and sun protection help keep it controlled." },
      { q: "Why does melasma return?", a: "Sun exposure and hormonal shifts are common triggers. Ongoing sun protection is the most important preventive step." },
      { q: "Is melasma treatment safe during pregnancy?", a: "Treatment is adjusted to be safe during pregnancy; sun protection and gentle care are the mainstay." },
      { q: "How long does treatment take?", a: "Melasma improves gradually over months. Consistency and patience are essential." },
    ],
    related: ["pigmentation", "pigmentation-peels", "chemical-peels", "general-dermatology"],
    relatedBlogs: ["why-is-my-skin-pigmented"],
    seo: {
      title: "Melasma Treatment in Krishnagiri | Pigmentation Specialist | AV Clinic",
      description:
        "Melasma treatment in Krishnagiri — manage stubborn facial brown patches with patient, multi-angle dermatology care and sun protection at AV Clinic.",
    },
  },
  {
    slug: "psoriasis",
    name: "Psoriasis",
    category: "Dermatology",
    categorySlug: "dermatology",
    blurb: "Long-term care for psoriasis to reduce flare-ups and discomfort.",
    tagline: "Living Well With Psoriasis.",
    intro:
      "Psoriasis is a chronic skin condition causing red, scaly patches. It is not contagious and cannot be spread. Management focuses on controlling symptoms, reducing flare-ups and improving quality of life through a plan tailored to you.",
    image: img.serviceDermatologist,
    symptoms: [
      { name: "Scaly red patches", desc: "Well-defined patches with silvery scales, often on elbows and knees." },
      { name: "Itching", desc: "Mild to intense itching or burning." },
      { name: "Scalp involvement", desc: "Scaling and flakes on the scalp." },
      { name: "Nail changes", desc: "Pitting, thickening or ridging of the nails." },
      { name: "Recurrent flare-ups", desc: "Symptoms that come and go over time." },
    ],
    causes: [
      "An overactive immune response",
      "Genetic predisposition",
      "Triggers such as stress, infection and skin injury",
      "Certain medications",
      "Climate changes",
    ],
    riskFactors: [
      "Family history of psoriasis",
      "Stress",
      "Infections (e.g., strep throat)",
      "Skin injury or irritation",
    ],
    whenToSee: [
      "Patches are spreading or painful",
      "Symptoms affect daily life or sleep",
      "You are unsure whether it is psoriasis",
      "Scalp or nails are involved",
    ],
    diagnosisSteps: [
      { title: "Clinical Examination", desc: "The skin, scalp and nails are examined." },
      { title: "History Review", desc: "Triggers and family history are discussed." },
      { title: "Diagnosis Confirmation", desc: "Psoriasis is usually diagnosed clinically." },
      { title: "Management Plan", desc: "A treatment and trigger-avoidance plan is agreed." },
    ],
    treatment: {
      medical: [
        "Topical treatments to reduce scaling and inflammation",
        "Prescribed care matched to severity",
      ],
      procedures: [],
      lifestyle: [
        "Identifying and avoiding personal triggers",
        "Gentle skin and scalp care",
        "Managing stress",
      ],
      followUp:
        "Psoriasis is a long-term condition. Regular review helps keep flare-ups controlled and treatment appropriate.",
    },
    myths: [
      { myth: "Psoriasis is contagious.", fact: "Psoriasis is not contagious and cannot spread from person to person." },
      { myth: "Psoriasis is just dry skin.", fact: "Psoriasis is an immune-related condition that needs appropriate long-term management." },
    ],
    prevention: [
      "Manage stress",
      "Moisturise to reduce dryness",
      "Avoid known triggers",
      "Protect skin from injury",
    ],
    management: [
      "Confirming the diagnosis",
      "Treatment to reduce scaling and inflammation",
      "Trigger identification and avoidance",
      "Ongoing monitoring and support",
    ],
    faqs: [
      { q: "Is psoriasis contagious?", a: "No. Psoriasis is not contagious and cannot spread from person to person." },
      { q: "Can psoriasis affect the scalp?", a: "Yes, scalp psoriasis is common and causes scaling and flakes. It is manageable with appropriate care." },
      { q: "Is there a permanent cure?", a: "There is no permanent cure, but psoriasis can often be well controlled with appropriate care." },
      { q: "Can stress trigger a flare-up?", a: "Yes, stress is a common trigger. Managing stress can help reduce flare-ups." },
    ],
    related: ["general-dermatology", "eczema", "scalp-disorders"],
    relatedBlogs: ["psoriasis-understanding-and-managing"],
    seo: {
      title: "Psoriasis Treatment in Krishnagiri | Skin Specialist | AV Clinic",
      description:
        "Psoriasis treatment in Krishnagiri — reduce flare-ups, scaling and discomfort with long-term dermatology care from a skin specialist at AV Clinic.",
    },
  },
  {
    slug: "eczema",
    name: "Eczema",
    category: "Dermatology",
    categorySlug: "dermatology",
    blurb: "Soothing irritated, itchy skin and reducing eczema flare-ups.",
    tagline: "Calmer Skin. Less Itch.",
    intro:
      "Eczema (dermatitis) causes dry, itchy, inflamed skin and can affect people of any age. AV Clinic helps manage symptoms with appropriate skin care and treatment while identifying common triggers to reduce flare-ups.",
    image: img.serviceDermatologist,
    symptoms: [
      { name: "Dry, itchy skin", desc: "Persistent itching that may worsen at night." },
      { name: "Red, inflamed patches", desc: "Areas of redness, often in flexures like elbows and knees." },
      { name: "Cracking or weeping", desc: "Dry skin that cracks, or areas that weep when severe." },
      { name: "Recurrent flare-ups", desc: "Symptoms that improve then return." },
    ],
    causes: [
      "A weakened skin barrier",
      "Irritants such as soaps and detergents",
      "Allergens and dust",
      "Weather changes",
      "Stress",
    ],
    riskFactors: [
      "Personal or family history of eczema, asthma or allergy",
      "Sensitive or dry skin",
      "Frequent exposure to irritants",
    ],
    whenToSee: [
      "Eczema is severe, painful or widespread",
      "Itching disturbs sleep",
      "Basic moisturising is not helping",
      "Signs of infection develop",
    ],
    diagnosisSteps: [
      { title: "Examination", desc: "The affected skin is examined." },
      { title: "Trigger Discussion", desc: "Possible irritants and allergens are reviewed." },
      { title: "Skin-Care Review", desc: "Current cleansing and moisturising are assessed." },
      { title: "Management Plan", desc: "A care and treatment plan is agreed." },
    ],
    treatment: {
      medical: [
        "Anti-inflammatory treatment as needed",
        "Moisturisers suited to your skin",
      ],
      procedures: [],
      lifestyle: [
        "Gentle cleansing and regular moisturising",
        "Avoiding harsh soaps and known irritants",
        "Wearing soft fabrics",
      ],
      followUp:
        "Eczema needs ongoing care. Review helps adjust treatment and prevent frequent flare-ups.",
    },
    myths: [
      { myth: "Eczema is contagious.", fact: "No. Eczema is not contagious and cannot be passed between people." },
      { myth: "Eczema only affects children.", fact: "Eczema can occur at any age and can first appear in adulthood." },
    ],
    prevention: [
      "Moisturise regularly",
      "Avoid known irritants",
      "Keep baths lukewarm and brief",
      "Manage stress",
    ],
    management: [
      "Evaluation of skin and possible triggers",
      "Moisturising and barrier-care advice",
      "Anti-inflammatory treatment as needed",
      "Guidance to prevent flare-ups",
    ],
    faqs: [
      { q: "Can eczema be managed without medication?", a: "Good skin care and trigger avoidance can help a great deal. When needed, prescribed treatments help control flare-ups." },
      { q: "What triggers eczema flare-ups?", a: "Common triggers include soaps, detergents, heat, dust, stress and weather changes." },
      { q: "Is eczema the same as an allergy?", a: "Eczema is not an allergy itself, but allergens can trigger or worsen it in some people." },
    ],
    related: ["general-dermatology", "skin-allergies", "psoriasis"],
    relatedBlogs: ["eczema-itchy-skin-what-you-can-do"],
    seo: {
      title: "Eczema Treatment in Krishnagiri | Dermatologist | AV Clinic",
      description:
        "Eczema treatment in Krishnagiri — soothe itchy, irritated skin and reduce flare-ups with dermatology care at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "fungal-infections",
    name: "Fungal Infections",
    category: "Dermatology",
    categorySlug: "dermatology",
    blurb: "Clear, effective care for ringworm, fungal skin and nail infections.",
    tagline: "Clear Skin, The Right Way.",
    intro:
      "Fungal infections of the skin and nails are common, uncomfortable and very treatable. Correct diagnosis and completing the full course of treatment are the keys to clearing the infection and preventing it from returning.",
    image: img.serviceDermatologist,
    symptoms: [
      { name: "Ringworm (tinea)", desc: "Ring-shaped, scaly, itchy patches." },
      { name: "Itchy fungal rash", desc: "Red, scaly areas, often in moist skin folds." },
      { name: "Thickened or discoloured nails", desc: "Nails that become yellow, thick or brittle." },
      { name: "Cracking or peeling", desc: "Peeling and itching between toes (athlete's foot)." },
    ],
    causes: [
      "Fungal organisms on warm, moist skin",
      "Excess sweating",
      "Tight or damp clothing",
      "Sharing towels or footwear",
      "Compromised skin barrier",
    ],
    riskFactors: [
      "Warm, humid climate",
      "Excess sweating",
      "Tight or synthetic footwear",
      "Diabetes or weakened immunity",
      "Contact with infected surfaces",
    ],
    whenToSee: [
      "The rash is spreading or not improving",
      "Itching is severe",
      "Nails are thickened or discoloured",
      "The infection keeps returning",
    ],
    diagnosisSteps: [
      { title: "Examination", desc: "The affected skin or nail is examined." },
      { title: "Skin Scraping", desc: "A simple scraping may be checked to confirm fungus." },
      { title: "Diagnosis", desc: "The type of fungal infection is identified." },
      { title: "Treatment Plan", desc: "The right antifungal and hygiene advice are given." },
    ],
    treatment: {
      medical: [
        "Antifungal creams or oral treatment as appropriate",
        "Guidance on completing the full course",
      ],
      procedures: [],
      lifestyle: [
        "Keeping skin clean and dry",
        "Wearing breathable clothing and footwear",
        "Not sharing towels or shoes",
      ],
      followUp:
        "Completing the full treatment course is essential. Review may be needed for stubborn or nail infections.",
    },
    myths: [
      { myth: "Stopping treatment when the rash clears is fine.", fact: "Stopping early is a common cause of recurrence. The full course should be completed." },
      { myth: "Fungal infections are only caught from dirty places.", fact: "Fungi thrive in warm, moist conditions anywhere; good hygiene reduces risk." },
    ],
    prevention: [
      "Keep skin dry",
      "Wear clean, breathable clothing",
      "Avoid sharing towels and footwear",
      "Change socks regularly",
    ],
    management: [
      "Confirming the type of fungal infection",
      "Appropriate antifungal treatment",
      "Hygiene and prevention advice",
      "Completion of full treatment course",
    ],
    faqs: [
      { q: "Why does my fungal infection keep returning?", a: "Stopping treatment early is a common cause of recurrence. Following the full course and good hygiene practices help prevent return." },
      { q: "How long does nail fungus treatment take?", a: "Nail infections take longer to treat, often several months, and require consistency." },
      { q: "Can I treat ringworm at home?", a: "Mild cases may respond to hygiene and anti-fungal care, but persistent infections need proper diagnosis and treatment." },
    ],
    related: ["general-dermatology", "nail-disorders"],
    relatedBlogs: ["why-see-a-dermatologist-in-krishnagiri"],
    seo: {
      title: "Fungal Infection Treatment in Krishnagiri | AV Clinic",
      description:
        "Fungal skin and nail infection treatment in Krishnagiri — clear ringworm and fungal infections effectively with dermatology care at AV Clinic.",
    },
  },
  {
    slug: "vitiligo",
    name: "Vitiligo",
    category: "Dermatology",
    categorySlug: "dermatology",
    blurb: "Compassionate care for the white patches of vitiligo.",
    tagline: "Supportive, Realistic Care for Vitiligo.",
    intro:
      "Vitiligo causes loss of pigment, leading to white patches on the skin. It is not contagious and is not harmful physically, but it can affect confidence. Management aims to restore colour where possible and to provide realistic, supportive care.",
    image: img.serviceDermatologist,
    symptoms: [
      { name: "White patches", desc: "Well-defined areas of lost skin colour." },
      { name: "Symmetrical patches", desc: "Patches often appear on both sides of the body." },
      { name: "Hair depigmentation", desc: "Hair in affected areas may turn white." },
      { name: "Progressive spread", desc: "Patches may grow or new ones may appear over time." },
    ],
    causes: [
      "Loss of pigment-producing cells",
      "Autoimmune response",
      "Genetic predisposition",
      "Triggering factors such as stress or skin injury",
    ],
    riskFactors: [
      "Family history of vitiligo or autoimmune conditions",
      "Personal history of autoimmune disease",
    ],
    whenToSee: [
      "White patches are new or spreading",
      "Patches affect your confidence",
      "You want a clear diagnosis and plan",
    ],
    diagnosisSteps: [
      { title: "Examination", desc: "The patches are examined, often under a wood's lamp." },
      { title: "Activity Assessment", desc: "Whether the condition is stable or active is assessed." },
      { title: "Discussion", desc: "Goals and realistic expectations are discussed." },
      { title: "Care Plan", desc: "Treatment and supportive options are agreed." },
    ],
    treatment: {
      medical: [
        "Topical treatment to help restore pigment",
        "Care matched to the extent and activity",
      ],
      procedures: [],
      lifestyle: [
        "Sun protection for affected skin",
        "Emotional support and patient education",
      ],
      followUp:
        "Repigmentation is gradual. Regular review helps monitor progress and adjust treatment.",
    },
    myths: [
      { myth: "Vitiligo is contagious.", fact: "Vitiligo is not contagious and cannot spread to others." },
      { myth: "Vitiligo is a form of skin cancer.", fact: "Vitiligo is a pigment disorder and is not a form of skin cancer." },
    ],
    prevention: [
      "Protect white patches from sunburn",
      "Use sunscreen on affected skin",
      "Seek support if it affects confidence",
    ],
    management: [
      "Assessment of extent and activity",
      "Treatment to help restore pigment",
      "Sun protection for affected skin",
      "Emotional support and education",
    ],
    faqs: [
      { q: "Is vitiligo curable?", a: "Treatment can help repigment patches, but results vary and it may recur. A dermatologist will advise realistic expectations." },
      { q: "Can vitiligo spread?", a: "Vitiligo can progress over time, but the rate and extent vary greatly between people." },
      { q: "Is vitiligo dangerous?", a: "No, vitiligo is not harmful physically, but the affected skin is more prone to sunburn." },
    ],
    related: ["general-dermatology"],
    seo: {
      title: "Vitiligo Treatment in Krishnagiri | Skin Specialist | AV Clinic",
      description:
        "Vitiligo treatment in Krishnagiri — compassionate, realistic dermatology care for white patches. Specialist evaluation and support at AV Clinic.",
    },
  },
  {
    slug: "urticaria",
    name: "Urticaria",
    category: "Dermatology",
    categorySlug: "dermatology",
    blurb: "Identifying and managing the raised, itchy welts of urticaria.",
    tagline: "Finding the Trigger. Easing the Itch.",
    intro:
      "Urticaria (hives) causes raised, itchy welts that can appear suddenly and vary in size. It can be acute or recurrent. AV Clinic helps identify possible triggers and control symptoms effectively and safely.",
    image: img.serviceDermatologist,
    symptoms: [
      { name: "Raised itchy welts", desc: "Pink or red raised areas that itch." },
      { name: "Recurrent hives", desc: "Welts that come and go, often lasting hours." },
      { name: "Swelling", desc: "Deep swelling of lips, eyelids or hands (angioedema) in some cases." },
    ],
    causes: [
      "Foods and medications",
      "Infections",
      "Stress",
      "Heat, cold or pressure",
      "Unknown causes (idiopathic)",
    ],
    riskFactors: [
      "Previous allergic reactions",
      "Autoimmune conditions",
      "Stress",
      "Certain medications",
    ],
    whenToSee: [
      "Hives are recurrent or persistent",
      "Swelling involves the face or throat",
      "Hives interfere with daily life",
      "You suspect an allergy",
    ],
    diagnosisSteps: [
      { title: "History & Pattern", desc: "When hives appear and what triggers them are reviewed." },
      { title: "Trigger Assessment", desc: "Foods, medicines and exposures are discussed." },
      { title: "Investigation When Needed", desc: "Tests are used selectively, not for every patient." },
      { title: "Management Plan", desc: "Symptom control and avoidance advice are given." },
    ],
    treatment: {
      medical: [
        "Antihistamine and symptom-control treatment",
        "Plans suited to the pattern of hives",
      ],
      procedures: [],
      lifestyle: [
        "Avoiding identified triggers",
        "Managing stress",
        "Keeping a symptom diary",
      ],
      followUp:
        "Recurring hives may need review to adjust treatment and identify patterns over time.",
    },
    myths: [
      { myth: "Hives are always a food allergy.", fact: "Hives can be caused by many things including infections, stress, heat and medicines — not just food." },
    ],
    prevention: [
      "Avoid known triggers",
      "Manage stress",
      "Record flare-ups to spot patterns",
    ],
    management: [
      "Evaluation of possible triggers",
      "Symptom-control treatment",
      "Avoidance guidance",
      "Follow-up for recurring cases",
    ],
    faqs: [
      { q: "What causes hives?", a: "Hives can be triggered by foods, medications, infections, stress or unknown causes. Identifying the pattern helps guide management." },
      { q: "How long do hives last?", a: "Individual welts usually fade within a day, but new ones can appear for weeks in recurrent cases." },
      { q: "When should hives be treated as urgent?", a: "If there is swelling of the lips, tongue or throat, or difficulty breathing, seek urgent medical care." },
    ],
    related: ["general-dermatology", "skin-allergies"],
    seo: {
      title: "Urticaria (Hives) Treatment in Krishnagiri | AV Clinic",
      description:
        "Urticaria and hives treatment in Krishnagiri — identify triggers and control itchy welts with dermatology care at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "skin-allergies",
    name: "Skin Allergies",
    category: "Dermatology",
    categorySlug: "dermatology",
    blurb: "Finding the cause of allergic skin reactions and soothing symptoms.",
    tagline: "Uncovering the Cause of Your Skin Reaction.",
    intro:
      "Allergic skin reactions can cause redness, itching, rashes and swelling. Care focuses on identifying likely triggers and controlling symptoms, so you can avoid reactions and keep your skin comfortable.",
    image: img.serviceDermatologist,
    symptoms: [
      { name: "Contact rash", desc: "Red, itchy rash where skin touches a product or material." },
      { name: "Allergic itching", desc: "Intense itching over affected areas." },
      { name: "Swelling", desc: "Localised swelling after exposure." },
      { name: "Blisters or weeping", desc: "In more severe reactions, small blisters may form." },
    ],
    causes: [
      "Cosmetics and skin-care products",
      "Nickel and jewellery",
      "Soaps, detergents and fabrics",
      "Plants and latex",
      "Medications",
    ],
    riskFactors: [
      "Sensitive skin",
      "History of eczema or other allergies",
      "Frequent use of multiple products",
    ],
    whenToSee: [
      "The rash is spreading or severe",
      "Itching is not controlled",
      "You are unsure of the cause",
      "Symptoms keep returning",
    ],
    diagnosisSteps: [
      { title: "History & Pattern", desc: "Where and when the reaction occurs is reviewed." },
      { title: "Product Review", desc: "Cosmetics and products are discussed in detail." },
      { title: "Patch Testing", desc: "In some cases, patch testing identifies specific triggers." },
      { title: "Avoidance Plan", desc: "You are guided to avoid likely allergens and soothe symptoms." },
    ],
    treatment: {
      medical: [
        "Symptom-relief treatment for itching and redness",
        "Appropriate anti-inflammatory care",
      ],
      procedures: [],
      lifestyle: [
        "Avoiding identified allergens",
        "Gentle skin care for sensitive skin",
      ],
      followUp:
        "Review helps confirm triggers and adjust care, especially for recurring reactions.",
    },
    myths: [
      { myth: "All reactions happen instantly.", fact: "Some allergic reactions appear hours or even days after exposure, making the cause hard to spot." },
    ],
    prevention: [
      "Patch-test new products",
      "Choose fragrance-free, gentle products",
      "Avoid known allergens",
    ],
    management: [
      "History and pattern assessment",
      "Avoidance of likely allergens",
      "Symptom-relief treatment",
      "Skin-care advice for sensitive skin",
    ],
    faqs: [
      { q: "How are skin allergies diagnosed?", a: "A detailed history of when and where the rash appears is key. Sometimes patch testing is used to identify specific triggers." },
      { q: "Can a new product cause a delayed reaction?", a: "Yes, allergic reactions can appear hours to days after first use, so new products should be introduced gradually." },
    ],
    related: ["general-dermatology", "eczema"],
    relatedBlogs: ["eczema-itchy-skin-what-you-can-do"],
    seo: {
      title: "Skin Allergy Treatment in Krishnagiri | Dermatologist | AV Clinic",
      description:
        "Skin allergy treatment in Krishnagiri — identify triggers and soothe allergic skin reactions with dermatology care at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "nail-disorders",
    name: "Nail Disorders",
    category: "Dermatology",
    categorySlug: "dermatology",
    blurb: "Evaluation and care for thickened, discoloured or damaged nails.",
    tagline: "Healthier Nails, Inside and Out.",
    intro:
      "Nails can reflect both local problems and general health. AV Clinic evaluates nail changes such as thickening, discolouration, ridges and fungal infection, and treats them appropriately.",
    image: img.serviceDermatologist,
    symptoms: [
      { name: "Thickened nails", desc: "Nails that become thick, hard or difficult to trim." },
      { name: "Discolouration", desc: "Yellow, white or dark discolouration." },
      { name: "Fungal infection", desc: "Crumbling, thickened nails with debris beneath." },
      { name: "Ridges or brittleness", desc: "Vertical ridges, splits or breaking." },
    ],
    causes: [
      "Fungal infection",
      "Injury or repeated pressure",
      "Skin conditions such as psoriasis",
      "Ageing",
      "Nutritional or general health factors",
    ],
    riskFactors: [
      "Warm, damp environments",
      "Diabetes",
      "Repeated trauma to nails",
      "Psoriasis or eczema",
    ],
    whenToSee: [
      "Nails are painful or lifting",
      "Discolouration persists",
      "Nails are thickened and hard to manage",
      "You are unsure of the cause",
    ],
    diagnosisSteps: [
      { title: "Examination", desc: "The nails and surrounding skin are examined." },
      { title: "History Review", desc: "Habits, footwear and health are considered." },
      { title: "Sampling When Needed", desc: "A nail sample may confirm a fungal infection." },
      { title: "Care Plan", desc: "Treatment and nail-care advice are given." },
    ],
    treatment: {
      medical: [
        "Treatment of fungal or other nail conditions",
        "Care matched to the underlying cause",
      ],
      procedures: [],
      lifestyle: [
        "Keeping nails clean and dry",
        "Trimming nails properly",
        "Wearing breathable footwear",
      ],
      followUp:
        "Nail treatment, especially for fungus, takes time and needs consistency and regular review.",
    },
    myths: [
      { myth: "Yellow, thick nails are always fungus.", fact: "Several conditions cause nail changes. Proper evaluation determines the cause." },
    ],
    prevention: [
      "Keep nails clean and dry",
      "Avoid sharing nail tools",
      "Protect nails from injury",
    ],
    management: [
      "Examination of nails and surrounding skin",
      "Treatment of fungal or other nail conditions",
      "Advice on nail care and hygiene",
    ],
    faqs: [
      { q: "Can fungal nail infection be treated?", a: "Yes, but treatment takes time and requires consistency. A dermatologist will advise the most suitable approach." },
      { q: "How long does nail treatment take?", a: "Nails grow slowly, so improvement can take several months. Consistent treatment is important." },
    ],
    related: ["general-dermatology", "fungal-infections"],
    seo: {
      title: "Nail Disorder Treatment in Krishnagiri | AV Clinic",
      description:
        "Nail disorder treatment in Krishnagiri — thickened, discoloured or fungal nails evaluated and cared for by a dermatologist at AV Clinic.",
    },
  },
  {
    slug: "mole-skin-lesion-evaluation",
    name: "Mole & Skin Lesion Evaluation",
    category: "Dermatology",
    categorySlug: "dermatology",
    blurb: "Reassuring evaluation of moles and skin growths.",
    tagline: "Peace of Mind About Your Moles.",
    intro:
      "Moles and skin lesions are very common. AV Clinic provides careful evaluation to confirm they are benign and to advise on any that need attention or review over time.",
    image: img.serviceDermatologist,
    symptoms: [
      { name: "Changing mole", desc: "A mole that changes in size, shape or colour." },
      { name: "New skin growth", desc: "A new or unusual growth on the skin." },
      { name: "Bleeding or crusting", desc: "A lesion that bleeds, crusts or does not heal." },
      { name: "Irregular borders", desc: "Asymmetrical moles with uneven edges." },
    ],
    causes: [
      "Normal skin pigment cells",
      "Sun exposure",
      "Genetics",
      "Ageing",
    ],
    whenToSee: [
      "A mole changes in size, shape or colour",
      "A lesion bleeds, itches or crusts",
      "You have a new, unusual growth",
      "You have many moles or a family history of skin issues",
    ],
    diagnosisSteps: [
      { title: "Careful Examination", desc: "The lesion is examined closely." },
      { title: "Dermatoscopy", desc: "A magnifying tool may be used to assess details." },
      { title: "Reassurance or Review", desc: "Most moles are benign and simply monitored." },
      { title: "Advice", desc: "You are told what to watch for and when to return." },
    ],
    treatment: {
      medical: [],
      procedures: ["Removal of bothersome lesions where appropriate"],
      lifestyle: ["Regular self-checks", "Sun protection"],
      followUp:
        "Benign moles may only need periodic review. Any concerning changes should be re-evaluated promptly.",
    },
    myths: [
      { myth: "Every mole must be removed.", fact: "Most moles are benign and only need monitoring, not removal." },
    ],
    warningSigns: [
      "Asymmetry or irregular borders",
      "Rapid change in size or colour",
      "Bleeding, itching or crusting",
    ],
    management: [
      "Careful examination of the lesion",
      "Reassurance for benign findings",
      "Advice on when to seek re-review",
    ],
    faqs: [
      { q: "When should a mole be checked?", a: "Any mole that changes in size, shape or colour, bleeds, or looks unusual should be evaluated promptly." },
      { q: "Are most moles dangerous?", a: "No. The vast majority of moles are benign. Evaluation provides reassurance and advice." },
    ],
    related: ["general-dermatology", "skin-tags"],
    seo: {
      title: "Mole & Skin Lesion Evaluation in Krishnagiri | AV Clinic",
      description:
        "Mole and skin lesion evaluation in Krishnagiri — careful, reassuring dermatology assessment of moles and skin growths at AV Clinic.",
    },
  },
  {
    slug: "skin-tags",
    name: "Skin Tags",
    category: "Dermatology",
    categorySlug: "dermatology",
    blurb: "Safe assessment and removal of bothersome skin tags.",
    tagline: "Safe Removal of Bothersome Skin Tags.",
    intro:
      "Skin tags are small, benign growths that commonly appear in skin folds. When they are irritating or cosmetically bothersome, they can be safely assessed and removed.",
    image: img.serviceDermatologist,
    symptoms: [
      { name: "Small fleshy growths", desc: "Soft, flesh-coloured growths on a thin stalk." },
      { name: "Irritation from friction", desc: "Discomfort when rubbing against clothing or jewellery." },
      { name: "Common locations", desc: "Neck, eyelids, armpits and groin folds." },
    ],
    causes: [
      "Friction from skin rubbing",
      "Genetics",
      "Ageing",
      "Weight changes",
    ],
    riskFactors: [
      "Being overweight",
      "Ageing",
      "Family history",
      "Pregnancy",
    ],
    whenToSee: [
      "A growth bleeds, grows rapidly or changes",
      "A tag is repeatedly irritated",
      "You want reassurance it is benign",
    ],
    diagnosisSteps: [
      { title: "Examination", desc: "The growth is examined to confirm it is a benign skin tag." },
      { title: "Discussion", desc: "Whether removal is needed or desired is discussed." },
      { title: "Safe Removal", desc: "Removal is performed when appropriate." },
      { title: "Aftercare", desc: "Simple aftercare advice is given." },
    ],
    treatment: {
      medical: [],
      procedures: ["Safe removal of bothersome skin tags"],
      lifestyle: ["Reducing friction where possible"],
      followUp:
        "Skin tags are benign. Once removed, no routine follow-up is usually needed unless new ones appear.",
    },
    myths: [
      { myth: "Skin tags are dangerous.", fact: "No. Skin tags are benign. Removal is done for comfort or cosmetic reasons." },
    ],
    management: [
      "Confirmation that the growth is a benign skin tag",
      "Safe removal where appropriate",
      "Aftercare advice",
    ],
    faqs: [
      { q: "Are skin tags dangerous?", a: "No, skin tags are benign. Removal is done for comfort or cosmetic reasons." },
      { q: "Do skin tags need removal?", a: "No, they are harmless. Removal is only needed if they are irritating or you wish to remove them." },
    ],
    related: ["general-dermatology", "mole-skin-lesion-evaluation"],
    seo: {
      title: "Skin Tag Removal in Krishnagiri | Dermatologist | AV Clinic",
      description:
        "Safe skin tag assessment and removal in Krishnagiri. Benign skin growths removed comfortably by a dermatologist at AV Clinic.",
    },
  },
  {
    slug: "warts-skin-tag-removal",
    name: "Warts & Skin Tag Removal",
    category: "Dermatology",
    categorySlug: "dermatology",
    blurb: "Effective removal of common warts and benign skin tags.",
    tagline: "Clear Skin. Safe Removal.",
    intro:
      "Warts and skin tags are common, usually benign skin growths that can be annoying or bothersome. AV Clinic provides safe assessment and removal using appropriate dermatological methods, matched to the type and location of the growth.",
    image: img.svcWartsSkinTags,
    symptoms: [
      { name: "Common warts", desc: "Rough, raised growths, often on the hands and fingers." },
      { name: "Flat warts", desc: "Small, smooth, slightly raised growths." },
      { name: "Skin tags", desc: "Soft, flesh-coloured growths on a thin stalk." },
      { name: "Plantar warts", desc: "Warts on the soles of the feet that can be painful when walking." },
    ],
    causes: [
      "Viral infection (human papillomavirus)",
      "Genetics",
      "Friction and skin rubbing",
      "Moist environments",
    ],
    riskFactors: [
      "Skin-to-skin contact",
      "Warm, damp environments",
      "Weakened skin barrier",
      "Being overweight (for skin tags)",
    ],
    whenToSee: [
      "A wart is spreading or not improving",
      "A growth is painful or bleeding",
      "A growth changes or worries you",
      "You want safe professional removal",
    ],
    diagnosisSteps: [
      { title: "Examination", desc: "The growth is examined to confirm its type." },
      { title: "Discussion", desc: "Whether and how to remove it is discussed." },
      { title: "Treatment Selection", desc: "The most suitable removal method is chosen." },
      { title: "Aftercare", desc: "Simple aftercare and hygiene advice are given." },
    ],
    treatment: {
      medical: [],
      procedures: [
        "Safe removal using appropriate dermatological methods",
      ],
      lifestyle: [
        "Hygiene and avoiding picking",
        "Keeping the area clean and dry",
      ],
      followUp:
        "Warts can sometimes recur. Aftercare and hygiene advice help reduce the chance of recurrence. Skin tags are benign and usually need no follow-up after removal.",
    },
    myths: [
      { myth: "Warts are caused by touching frogs.", fact: "Warts are caused by a viral infection, not by touching frogs or other animals." },
      { myth: "Removing a wart causes it to spread.", fact: "Proper professional removal is safe and reduces the chance of spread." },
    ],
    prevention: [
      "Avoid picking at warts",
      "Keep skin clean and dry",
      "Do not share towels or footwear",
    ],
    management: [
      "Confirmation of the type of growth",
      "Safe, appropriate removal",
      "Hygiene and aftercare advice",
    ],
    faqs: [
      { q: "Are warts and skin tags dangerous?", a: "No. They are usually benign and harmless. Removal is done for comfort or cosmetic reasons." },
      { q: "Can warts come back after removal?", a: "Some warts can recur. Following hygiene and aftercare advice helps reduce the chance." },
      { q: "Is removal painful?", a: "Most removal procedures are well tolerated. Your dermatologist will explain what to expect." },
    ],
    related: ["skin-tags", "mole-skin-lesion-evaluation", "general-dermatology"],
    seo: {
      title: "Warts & Skin Tag Removal in Krishnagiri | Dermatologist | AV Clinic",
      description:
        "Warts and skin tag removal in Krishnagiri — safe assessment and removal of common benign skin growths by a dermatologist at AV Clinic.",
    },
  },
  {
    slug: "skin-cancer-evaluation",
    name: "Skin Cancer Evaluation",
    category: "Dermatology",
    categorySlug: "dermatology",
    blurb: "Careful evaluation of suspicious or changing skin lesions.",
    tagline: "Careful Evaluation, Early Peace of Mind.",
    intro:
      "Skin cancer evaluation is about identifying and assessing suspicious or changing skin lesions early. AV Clinic provides careful examination of moles and growths that look unusual, with clear guidance and appropriate next steps when needed.",
    image: img.svcSkinCancer,
    symptoms: [
      { name: "Asymmetrical mole", desc: "A mole where one half does not match the other." },
      { name: "Irregular border", desc: "A mole with uneven, jagged edges." },
      { name: "Colour change", desc: "A mole with multiple colours or uneven colour." },
      { name: "Enlarging lesion", desc: "A mole or growth that is growing quickly." },
      { name: "Bleeding or crusting", desc: "A lesion that bleeds, crusts or does not heal." },
    ],
    causes: [
      "Sun exposure over time",
      "Genetics and family history",
      "Multiple or unusual moles",
      "Ageing",
    ],
    riskFactors: [
      "Frequent or intense sun exposure",
      "History of sunburn",
      "Light skin that burns easily",
      "Family history of skin cancer",
      "Many moles or atypical moles",
    ],
    whenToSee: [
      "A mole changes in size, shape or colour",
      "A lesion is new, unusual or growing",
      "A growth bleeds, crusts or does not heal",
      "You have many moles or a family history",
    ],
    diagnosisSteps: [
      { title: "Careful Examination", desc: "The lesion is examined closely." },
      { title: "Dermatoscopy", desc: "A magnifying tool may be used to assess details." },
      { title: "Assessment", desc: "Whether monitoring, review or a sample is needed is decided." },
      { title: "Guidance", desc: "You receive clear advice and next steps." },
    ],
    treatment: {
      medical: [],
      procedures: [
        "Appropriate evaluation and referral when needed",
      ],
      lifestyle: [
        "Daily sun protection",
        "Regular self-checks",
        "Avoiding tanning",
      ],
      followUp:
        "Depending on the findings, further review or a specialist opinion may be recommended. Your dermatologist will explain what is needed.",
    },
    myths: [
      { myth: "All changing moles are cancer.", fact: "Most moles are benign and do not change. A careful evaluation determines whether a change needs attention." },
      { myth: "Skin cancer only affects older or light-skinned people.", fact: "Skin cancer can affect people of any age and skin type. Early evaluation is important for everyone." },
    ],
    warningSigns: [
      "A mole is growing rapidly",
      "A lesion is bleeding, crusting or not healing",
      "A mole has uneven colours or borders",
    ],
    management: [
      "Careful evaluation of the lesion",
      "Appropriate referral when indicated",
      "Advice on monitoring and self-checks",
    ],
    faqs: [
      { q: "When should a mole be checked?", a: "Any mole that changes in size, shape or colour, bleeds, crusts, or looks unusual should be evaluated promptly." },
      { q: "Does an unusual mole always mean cancer?", a: "No. Most unusual moles are benign. Evaluation provides reassurance and identifies anything that needs attention." },
      { q: "How can I reduce my risk?", a: "Practice daily sun protection, avoid tanning, and perform regular self-checks. Report any changing lesions promptly." },
    ],
    related: ["mole-skin-lesion-evaluation", "skin-tags", "general-dermatology"],
    seo: {
      title: "Skin Cancer Evaluation in Krishnagiri | Dermatologist | AV Clinic",
      description:
        "Skin cancer evaluation in Krishnagiri — careful assessment of suspicious or changing skin lesions by a dermatologist at AV Clinic. Book a consultation.",
    },
  },
];